// #32 — Recent plays: the last 30 posts a user played or watched, newest first, one entry per post, kept per user
// in localStorage (`recent_plays_<userId>`). No server. Every storage access is guarded: it may throw (private mode).
import type { WallPost } from "../api/content";
import { uploadThumb, uploadTitle } from "../components/wall/uploadRow";

export const RECENT_MAX = 30;

export type RecentWall = { kind: "GC"; name: string | null } | { kind: "USER"; ownerName: string };

export interface RecentPlay {
  postId: string;
  wall: RecentWall;
  title: string;
  thumbnailUrl: string | null;
  playedAt: number;
}

export const recentKey = (userId: string): string => `recent_plays_${userId}`;

function store(storage?: Storage): Storage | null {
  try {
    return storage ?? window.localStorage;
  } catch {
    return null;
  }
}

function isEntry(e: unknown): e is RecentPlay {
  if (typeof e !== "object" || e === null) return false;
  const o = e as Record<string, unknown>;
  const w = o.wall as Record<string, unknown> | null | undefined;
  const wallOk =
    typeof w === "object" &&
    w !== null &&
    ((w.kind === "GC" && (w.name === null || typeof w.name === "string")) || (w.kind === "USER" && typeof w.ownerName === "string"));
  return (
    typeof o.postId === "string" &&
    o.postId !== "" &&
    wallOk &&
    typeof o.title === "string" &&
    (o.thumbnailUrl === null || typeof o.thumbnailUrl === "string") &&
    typeof o.playedAt === "number" &&
    Number.isFinite(o.playedAt)
  );
}

/** A street-chat GC's conversation.name: an internal channel hash, never a name to show (recorded before the fix). */
const CONV_HASH = /^[0-9a-f]{32}$/i;

/**
 * [] on a storage error or bad JSON; malformed entries are dropped; duplicates and overflow are trimmed. A GC name
 * that is a conversation hash reads as unknown.
 */
export function readRecents(userId: string, storage?: Storage): RecentPlay[] {
  try {
    const raw = store(storage)?.getItem(recentKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const out: RecentPlay[] = [];
    for (const e of parsed) {
      if (!isEntry(e) || seen.has(e.postId)) continue;
      seen.add(e.postId);
      out.push(e.wall.kind === "GC" && e.wall.name && CONV_HASH.test(e.wall.name) ? { ...e, wall: { kind: "GC", name: null } } : e);
    }
    return out.slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

/** The entry first, any earlier entry of the same post removed, capped at RECENT_MAX. */
export function withRecent(list: RecentPlay[], entry: RecentPlay): RecentPlay[] {
  return [entry, ...list.filter((e) => e.postId !== entry.postId)].slice(0, RECENT_MAX);
}

export function writeRecents(userId: string, list: RecentPlay[], storage?: Storage): void {
  try {
    store(storage)?.setItem(recentKey(userId), JSON.stringify(list));
  } catch {
    /* storage unavailable: the in-memory list still works */
  }
}

export function clearRecents(userId: string, storage?: Storage): void {
  try {
    store(storage)?.removeItem(recentKey(userId));
  } catch {
    /* ignore */
  }
}

/** A play of `post`: title and thumbnail as My uploads shows them; a personal post's wall is its owner's name. */
export function recentOf(post: WallPost, wallName: string | null, now: number): RecentPlay {
  const wall: RecentWall =
    post.ownerType === "USER" ? { kind: "USER", ownerName: post.author.name } : { kind: "GC", name: wallName || post.wallName || null };
  // The raw thumbnail is stored; RecentPlaysPanel gates it (shownImage) when it renders.
  return { postId: post.id, wall, title: uploadTitle(post), thumbnailUrl: uploadThumb(post, true), playedAt: now };
}
